import React from 'react';
import { observable, computed, action } from 'mobx';
import { observer } from 'mobx-react';
import Grid from '@material-ui/core/Grid';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import CardActions from '@material-ui/core/CardActions';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import Accordion from '@material-ui/core/Accordion';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import Button from '@material-ui/core/Button';
import { withStyles } from '@material-ui/core/styles';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import LaunchIcon from '@material-ui/icons/Launch';
import Skeleton from '@material-ui/lab/Skeleton';

import Header from '../components/utils/Header';
import User from '../api/User';
import GlossaryEntry from '../api/GlossaryEntry';
import { ATHENA_VERSION } from '../globals'
import currentUser from '../store/currentUser';

const styles = theme => ({
  card: {
    maxWidth: theme.spacing(75),
  },
  athena_version: {
    color: 'grey',
  }
});


@withStyles(styles)
@observer
class HelpView extends React.Component {
  @observable moocCredentials = null;
  @observable entries = null;

  @action async componentDidMount() {
    [this.moocCredentials, this.entries] = await Promise.all([
      User.ajaxRequestData(
        'get', '/mooc_credentials/',
      ),
      GlossaryEntry.getList(),
    ]);
  }

  @computed get groupedEntries() {
    const categories = {};
    for (const entry of this.entries) {
      if (!(entry.category in categories)) {
        categories[entry.category] = [];
      }
      categories[entry.category].push(entry);
    }
    return Object.entries(categories);
  }

  renderEntries() {
    if (this.entries === null) {
      return <Grid item><Skeleton variant="rect" height={450} /></Grid>;
    }
    if (this.entries.length === 0) {
      return <Grid item>No glossary entry yet.</Grid>;
    }
    return this.groupedEntries.map(([category, entries]) =>
      <Grid item key={category}>
        <Typography variant="h6" gutterBottom>{category}</Typography>
        <div>
          {
            entries.map(entry =>
              <Accordion key={entry.name}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <strong>{entry.name}</strong>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container direction="column">
                    {entry.definition.split('\n').map((line, index) =>
                      <Grid item key={index}><em>{line}</em></Grid>
                    )}
                  </Grid>
                </AccordionDetails>
              </Accordion>
            )
          }
        </div>
      </Grid>
    );
  }

  renderLearningPlatform() {
    const { classes } = this.props;
    return (
      <Grid item>
        <Card elevation={2} className={classes.card}>
          <CardContent>
            <Grid container direction="column" spacing={2}>
              <Grid item>
                <Typography variant="h5">Learning platform</Typography>
              </Grid>
              <Grid item>
                <Typography variant="subtitle1">
                  For more information on how Athena works,
                  please go to the Athena Learning Platform
                </Typography>
              </Grid>
              <Grid item>
                <Typography variant="subtitle2">
                  When accessing the application, please use the following credentials:
                </Typography>
              </Grid>
              <Grid item>
                {
                  this.moocCredentials === null
                    ? <Skeleton variant="rect" width={300} height={55} />
                    : (
                      <Grid container spacing={2}>
                        <Grid item>
                          <TextField
                            label="Username"
                            variant="outlined"
                            value={this.moocCredentials.username}
                            fullWidth
                          />
                        </Grid>
                        <Grid item>
                          <TextField
                            label="Password"
                            variant="outlined"
                            value={this.moocCredentials.password}
                            fullWidth
                          />
                        </Grid>
                      </Grid>
                    )
                }
              </Grid>
            </Grid>
          </CardContent>
          <CardActions>
            <Button
              size="small"
              color="primary"
              component="a"
              href="https://connect.bcg.com/p-600927000/"
              target="_blank"
              startIcon={<LaunchIcon />}
            >
              Go to Athena learning platform
            </Button>
          </CardActions>
        </Card>
      </Grid>
    )
  }

  render() {
    const { classes } = this.props;
    return (
      <Grid container direction="column" spacing={4} wrap="nowrap">
        <Grid item>
          <Header title="Help" />
        </Grid>
        {this.renderLearningPlatform()}
        {this.renderEntries()}
        {currentUser.is_superuser && <Grid item className={classes.athena_version}>Athena Version: {ATHENA_VERSION}</Grid>}
      </Grid>
    );
  }
}


export default HelpView;
